import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { CUSTOMERS, SOURCES, formatDate, sortedEvents, summarize } from './customerTimeline';
import type { Source } from './customerTimeline';

const SITE_URL = 'https://www.greyfielddata.com';
const PATH = '/demos/customer-timeline';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

export default function CustomerTimelinePage() {
  const [picked, setPicked] = React.useState(0);
  const [hidden, setHidden] = React.useState<Source[]>([]);
  const customer = CUSTOMERS[picked];
  const summary = summarize(customer);
  const events = sortedEvents(customer, hidden);
  const badge = (s: Source) => SOURCES.find((x) => x.key === s)!;

  return (
    <BlogLayout wide>
      <SEO
        title="Unified customer timeline demo: Shopify, Klaviyo, Recharge, and ads in one view"
        description="A working demo of one customer record joining ad clicks, site visits, Shopify orders, Klaviyo emails and texts, Recharge subscription changes, and support tickets."
        url={`${SITE_URL}${PATH}`}
      />
      <BreadcrumbStructuredData items={[{ name: 'Home', url: SITE_URL }, { name: 'Demos', url: `${SITE_URL}/demos` }, { name: 'Customer timeline', url: `${SITE_URL}${PATH}` }]} />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/demos" className="hover:text-[#1a4e94]">Demos</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">Customer timeline</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">Demo · Synthetic data for Brightleaf, a made-up supplement brand</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">One customer, every touchpoint</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">Ads, site visits, orders, emails, texts, subscription changes, and support tickets, joined into one timeline per customer. This is what a unified customer view looks like once the data sits in one warehouse.</p>
      </header>

      <div className="mt-10 flex flex-wrap items-start gap-5">
        <nav aria-label="Customers" className="min-w-0 flex-[1_1_240px] rounded-3xl border border-slate-200 bg-white p-2.5 lg:max-w-[280px]">
          <p className="px-2.5 pb-2 pt-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Customers</p>
          {CUSTOMERS.map((c, i) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={i === picked}
              onClick={() => setPicked(i)}
              className={`mb-1 block min-h-[56px] w-full rounded-xl border px-3 py-2.5 text-left ${i === picked ? 'border-[#1a4e94] bg-[#eaf2fd]' : 'border-transparent hover:bg-slate-50'}`}
            >
              <span className="block font-semibold text-slate-950">{c.name}</span>
              <span className="mt-0.5 block text-sm text-slate-500">{c.tag}</span>
            </button>
          ))}
        </nav>

        <section aria-labelledby="customer-name" className="flex min-w-0 flex-[999_1_520px] flex-col gap-4">
          <div className="flex flex-wrap justify-between gap-5 rounded-3xl bg-[#0a1628] p-6 text-white">
            <div>
              <h2 id="customer-name" className="text-2xl font-semibold">{customer.name}</h2>
              <p className="mt-1.5 text-sm text-slate-300">{customer.email}{summary.firstOrder ? ` · first order ${formatDate(summary.firstOrder)}` : ''}</p>
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
              {[
                ['Orders', String(summary.orders)],
                ['Revenue', `$${summary.revenue.toFixed(0)}`],
                ['First touch', customer.channel],
                ['Status', customer.status],
              ].map(([label, value]) => (
                <div key={label}><dt className="text-xs text-[#93b4e6]">{label}</dt><dd className="mt-1 text-lg font-semibold">{value}</dd></div>
              ))}
            </dl>
          </div>

          <div role="group" aria-label="Show or hide sources" className="flex flex-wrap gap-2">
            {SOURCES.map((s) => {
              const on = !hidden.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setHidden((h) => (h.includes(s.key) ? h.filter((x) => x !== s.key) : [...h, s.key]))}
                  className={`inline-flex min-h-[40px] items-center rounded-full border px-3.5 text-sm font-medium ${on ? 'border-slate-300 bg-white text-slate-950' : 'border-slate-200 bg-white text-slate-400 line-through'}`}
                >
                  <span aria-hidden="true" className={`mr-2 h-2 w-2 rounded-full ${s.dot}`} />
                  {s.label}
                </button>
              );
            })}
          </div>

          <ol className="overflow-hidden rounded-3xl border border-slate-200 bg-white" aria-label={`Timeline for ${customer.name}, newest first`}>
            {events.map((e) => (
              <li key={`${e.date}-${e.title}`} className="grid gap-2 border-b border-slate-100 px-5 py-3.5 last:border-b-0 sm:grid-cols-[110px_120px_minmax(0,1fr)] sm:items-start sm:gap-4">
                <span className="text-sm tabular-nums text-slate-500">{formatDate(e.date)}</span>
                <span className={`justify-self-start rounded-full px-2.5 py-1 text-xs font-semibold ${badge(e.source).badge}`}>{badge(e.source).label}</span>
                <span className="leading-6"><strong className="font-semibold text-slate-950">{e.title}</strong> <span className="text-slate-500">{e.detail}</span></span>
              </li>
            ))}
            {events.length === 0 && <li className="px-5 py-8 text-center text-slate-500">All sources are hidden. Turn one back on above.</li>}
          </ol>
        </section>

        <aside aria-label="How this customer is joined" className="min-w-0 flex-[1_1_240px] rounded-3xl border border-slate-200 bg-white p-5 lg:max-w-[280px]">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#1a4e94]">How the records join</p>
          <ul className="mt-3 space-y-3 text-sm">
            {customer.ids.map((i) => (
              <li key={i.system}><div className="text-slate-500">{i.system}</div><div className="break-all font-mono text-slate-950">{i.id}</div></li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-6 text-slate-600">Matched on a cleaned, lowercased email first, then each system's customer ID. Ad touchpoints join through the UTM-tagged session before the first order.</p>
          <p className="mt-3 text-xs leading-5 text-slate-500">Sources shown: {SOURCES.map((s) => s.system).join(', ')}.</p>
        </aside>
      </div>

      <section className="mt-14 grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 p-6">
          <h2 className="text-xl font-semibold text-slate-950">How this is built</h2>
          <p className="mt-2.5 leading-7 text-slate-600">Each system lands in the warehouse on its own schedule. A customer table stitches the IDs together, and an events table puts every touchpoint in one shape: who, when, which system, what happened. The timeline is a simple query on top.</p>
          <Link to="/blog/how-to-track-customers-across-platforms" className="mt-4 inline-block font-semibold text-[#1a4e94] underline underline-offset-4">Read how to track customers across platforms</Link>
        </div>
        <div className="rounded-3xl bg-[#0a1628] p-6 text-white">
          <h2 className="text-xl font-semibold">Want this on your own customers?</h2>
          <p className="mt-2.5 leading-7 text-slate-300">Shopify, Klaviyo, Recharge, and your ad platforms into one customer view, with lifetime value and CAC by channel on top.</p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex min-h-[46px] items-center rounded-xl bg-white px-5 font-semibold text-[#0a1628] hover:bg-slate-100">Book a call</a>
        </div>
      </section>
    </BlogLayout>
  );
}
